import {
  CardButton,
  MainText,
  MainTitle,
  MySafeAreaView,
} from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import moment from "moment";
import React from "react";
import analytics from "@react-native-firebase/analytics";
import {
  StyleSheet,
  View,
  ScrollView,
  Platform,
  Linking,
  Image,
} from "react-native";
import {
  heightPercentageToDP,
  widthPercentageToDP,
} from "react-native-responsive-screen";
import Colors from "@microdoing/shared/constants/Colors";
import { useDispatch, useSelector } from "react-redux";
import {
  cancelAllNotifications,
  scheduleGoalNotif,
} from "@microdoing/shared/services/notifService";
import {
  updateUserWithoutResponseRequest,
  modifyUser,
} from "@microdoing/shared/services/user/actions";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import hasNotificationPermission from "@microdoing/shared/utils/hasNotificationPermission";
import { signupOnboardingEndRequest } from "@microdoing/shared/services/signup/actions";
import requestNotificationPermission from "@microdoing/shared/utils/requestNotificationPermission";
import CheckText from "./CheckText";
import { CommonActions } from "@react-navigation/native";
import { showAlert } from "@microdoing/shared/services/profileSettings/actions";
import { getSignupRequesting } from "@microdoing/shared/services/signup/selectors";
import theme from "app/Theme/Theme";

const styles = StyleSheet.create({
  main: {
    backgroundColor: theme.colors["neutral.white"],
    flexGrow: 1,
  },
  button: {
    alignSelf: "center",
    marginBottom: 20,
    marginTop: heightPercentageToDP("4.5%"),
    height: 55,
    width: widthPercentageToDP("85%"),
  },
  topText: {
    marginTop: heightPercentageToDP("2.5%"),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "column",
  },
  checkViews: {
    marginTop: heightPercentageToDP("2%"),
    marginBottom: heightPercentageToDP("2%"),
    alignSelf: "center",
    width: widthPercentageToDP("80%"),
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    flex: 1,
    alignItems: "center",
  },
  middleView: {
    display: "flex",
    flexDirection: "column",
    width: "80%",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
  scrollView: {
    justifyContent: "space-between",
  },
  image: {
    marginTop: 50,
    width: "100%",
    height: "28%",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 17,
  },
  checkText: {
    marginBottom: 28,
  },
});

const ClockNotif = require("../../../assets/images/bell.png");

const AllowNotificationsBellScreen = ({ navigation, route }) => {
  const isComingFromSignup = route?.params?.isComingFromSignup;
  const isComingFromFirstAction = route?.params?.isComingFromFirstAction;
  const requesting: boolean = useSelector(getSignupRequesting);

  const userId = useSelector(getUserId);

  const dispatch = useDispatch();

  const handlePressNext = async () => {
    await cancelAllNotifications();
    await scheduleGoalNotif();

    if (isComingFromSignup) {
      return dispatch(signupOnboardingEndRequest());
    } else if (isComingFromFirstAction) {
      dispatch(
        showAlert({
          title: i18n.t("modify-profile-success-title"),
          message: i18n.t("modify-profile-success-message"),
        }),
      );
      return navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "Home" }],
        }),
      );
    } else {
      return navigation.goBack();
    }
  };

  const handlePressAuthorize = async () => {
    try {
      const accepted = await requestNotificationPermission();
    } finally {
      const enabled = await hasNotificationPermission();
      if (enabled) {
        dispatch(
          updateUserWithoutResponseRequest({
            userId,
            hasNotificationsEnabled: true,
            hasNotificationsEnabledCheckDate: moment().toDate(),
          }),
        );
        dispatch(modifyUser({ hasNotificationsEnabled: true }));

        if (Platform.OS !== "web") {
          analytics().logEvent("press_accept_notifications", {
            screen: "allow-notification-bell-screen",
          });
        }
      } else {
        if (Platform.OS !== "web") {
          analytics().logEvent("press_reject_notifications", {
            screen: "allow-notification-bell-screen",
          });
        }
        if (Platform.OS === "ios") {
          Linking.canOpenURL("app-settings:")
            .then(supported => {
              if (!supported) {
                console.log("Can't handle settings url");
              } else {
                return Linking.openURL("app-settings:");
              }
            })
            .catch(err => console.error("An error occurred", err));
        } else {
          Linking.openSettings();
        }
      }
    }

    handlePressNext();
  };

  return (
    <MySafeAreaView style={styles.main}>
      <ScrollView contentContainerStyle={styles.scrollView}>
        <View style={styles.topText}>
          <MainTitle
            variant="l9-bold"
            textAlign="center"
            marginTop="xm"
            marginLeft="xm"
            marginRight="xm"
            color="neutral.500"
          >
            {i18n.t("activate-notifications")}
          </MainTitle>
          <MainText
            variant="l1-regular"
            marginTop="xxs"
            color="neutral.500"
            textAlign="center"
          >
            {i18n.t("don-t-miss-anything")}
          </MainText>
        </View>
        <View style={styles.middleView}>
          <Image
            source={ClockNotif}
            style={styles.image}
            resizeMode="contain"
          />
          <MainText variant="l4-bold" color="neutral.500" marginBottom="m">
            {i18n.t("allow-notifications")}
          </MainText>
          <MainText
            variant="l4-bold"
            textAlign="center"
            color="neutral.500"
            marginBottom="xl"
          >
            {i18n.t("explanation-notification")}
          </MainText>
          <View style={styles.checkViews}>
            <CheckText
              isSmall
              text={i18n.t("set-up-notifications")}
              isChecked
              style={styles.checkText}
            />
            <CheckText isSmall text={i18n.t("allow-notifications")} />
          </View>
        </View>
      </ScrollView>
      <CardButton
        buttonPropStyle={styles.button}
        onPress={handlePressAuthorize}
        text={i18n.t("next")}
        requesting={requesting}
      />
    </MySafeAreaView>
  );
};

export default AllowNotificationsBellScreen;
